import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { Icon } from 'ui';
import { Text, TEXT_VARIANTS } from 'ui/Typography';

import ClickAwayListener from 'components/shared/ClickAwayListener';

const SectionListItem = ({
  url,
  id,
  order,
  title,
  isPreviewing,
  hasCurrentUser,
  hasUnansweredQuestion,
  onClick,
  isFocusable,
  focus,
}) => {
  const ref = useRef();

  useEffect(() => {
    if (ref.current && focus) {
      ref.current.focus();
    }
  }, [focus]);

  return (
    <li>
      <a
        className="LessonHeaderSectionInfo--listItem d-f jc-sb ai-c wb-bw"
        href={url}
        onClick={e => onClick(e, id)}
        tabIndex={isFocusable ? 0 : -1}
        ref={ref}
      >
        <Text
          className="LessonHeaderSectionInfo--listItemTitle wb-ba ws-pr"
          variant={TEXT_VARIANTS.SUBHEADER}
        >
          {`${order}. ${title}`}
        </Text>
        {!isPreviewing && hasCurrentUser && hasUnansweredQuestion && (
          <Text
            className="Label Label--error f-sh-0"
            variant={TEXT_VARIANTS.INSTRUX_SMALL_SEMIBOLD_ERROR}
          >
            <Icon name="required" /> {t('common.required')}
          </Text>
        )}
      </a>
    </li>
  );
};

const LessonHeaderSectionList = ({
  section = {},
  sections,
  onClose,
  isOpen,
  isPreviewing,
  hasCurrentUser,
  focusOnOpen,
}) => {
  const onLinkClick = (e, sectionId) => {
    if (sectionId === section.id) {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <ClickAwayListener
      className={cn('LessonHeaderSectionInfo', {
        'LessonHeaderSectionInfo--active': isOpen,
      })}
      onClickAway={() => isOpen && onClose()}
      touchEvent={false}
    >
      <ol
        id="lesson-table-of-contents"
        className="LessonHeaderSectionInfo--list"
      >
        {sections.map(
          (
            { id, title: sectionTitle, order, url, hasUnansweredQuestion },
            index,
          ) => (
            <SectionListItem
              key={id}
              id={id}
              focus={index === 0 && isOpen && focusOnOpen}
              title={sectionTitle}
              order={order}
              url={url}
              isPreviewing={isPreviewing}
              hasCurrentUser={hasCurrentUser}
              hasUnansweredQuestion={hasUnansweredQuestion}
              onClick={onLinkClick}
              isFocusable={isOpen}
            />
          ),
        )}
      </ol>
    </ClickAwayListener>
  );
};

LessonHeaderSectionList.propTypes = {
  section: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      order: PropTypes.number,
      title: PropTypes.string,
      url: PropTypes.string,
      hasUnansweredQuestion: PropTypes.bool,
    }),
  ),
  onClose: PropTypes.func,
  isOpen: PropTypes.bool,
  isPreviewing: PropTypes.bool,
  hasCurrentUser: PropTypes.bool,
};

export default LessonHeaderSectionList;
